import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from './usersContext';
import api from './utils/api';
import StatusMessage from './components/StatusMessage';
import { useStatusMessage } from './hooks/useStatusMessage';
import './Auth.css';

function StripeConnectReturn() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userDoc, userStatus } = useUser();
  const statusMsg = useStatusMessage();
  const [isChecking, setIsChecking] = useState(true);
  const [accountStatus, setAccountStatus] = useState(null);

  useEffect(() => {
    // Only tutors should access this page
    if (userStatus === 'verified' && userDoc?.userType === 'tutor') {
      checkAccountStatus();
    } else if (userStatus === 'verified' && userDoc?.userType !== 'tutor') {
      // Non-tutors shouldn't be here
      navigate('/dashboard');
    } else if (userStatus !== 'verified') {
      // Non-verified users go to auth
      navigate('/Auth');
    }
  }, [userStatus, userDoc]);

  const checkAccountStatus = async () => {
    try {
      setIsChecking(true);
      
      // Check the status of their Connect account
      const statusResponse = await api.post('/api/checkStripeConnectStatus', {});
      const statusData = await statusResponse.json();
      
      if (!statusResponse.ok) {
        throw new Error(statusData.message || 'Failed to check account status');
      }
      
      setAccountStatus(statusData.accountStatus);
      
      if (statusData.accountStatus?.chargesEnabled && statusData.accountStatus?.payoutsEnabled) {
        // Account is fully set up!
        statusMsg.showSuccess('Payment account setup complete! Redirecting to your dashboard...');
        setTimeout(() => navigate('/dashboard'), 2000);
      } else if (statusData.accountStatus?.detailsSubmitted) {
        // Details submitted but verification pending
        statusMsg.showInfo('Your payment account is under review. You can start tutoring while we verify your information.');
        setTimeout(() => navigate('/dashboard'), 3000);
      } else {
        // Onboarding not complete
        statusMsg.showWarning('Payment setup incomplete. Redirecting back to complete setup...');
        setTimeout(() => navigate('/stripe-connect-onboarding'), 2000);
      }
      
    } catch (error) {
      console.error('Error checking account status:', error);
      statusMsg.showError('Failed to verify payment account status');
      setIsChecking(false);
      
      // Redirect back to onboarding after delay
      setTimeout(() => navigate('/stripe-connect-onboarding'), 3000);
    }
  };

  const getStatusMessage = () => {
    if (!accountStatus) return 'Checking your payment account status...';
    
    if (accountStatus.chargesEnabled && accountStatus.payoutsEnabled) {
      return 'Your payment account is fully activated! Redirecting to dashboard...';
    } else if (accountStatus.detailsSubmitted) {
      return 'Your information has been submitted and is being reviewed. You can start tutoring while we verify your details.';
    } else {
      return 'Payment setup is incomplete. Redirecting to complete setup...';
    }
  };

  const getStatusColor = () => {
    if (!accountStatus) return '#1976d2';
    
    if (accountStatus.chargesEnabled && accountStatus.payoutsEnabled) {
      return '#4caf50';
    } else if (accountStatus.detailsSubmitted) {
      return '#ff9800';
    } else {
      return '#f44336';
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-form-container">
        <h2 className="auth-title">Verifying Payment Setup</h2>
        
        <div className="auth-message" style={{ textAlign: 'center' }}>
          {isChecking && (
            <>
              <div className="loading-spinner" style={{ marginBottom: '1rem' }}></div>
              <p>{getStatusMessage()}</p>
            </>
          )}
          
          {!isChecking && accountStatus && (
            <div>
              <div style={{ 
                fontSize: '3rem', 
                marginBottom: '1rem',
                color: getStatusColor()
              }}>
                {accountStatus.chargesEnabled && accountStatus.payoutsEnabled ? '✓' : 
                 accountStatus.detailsSubmitted ? '⏳' : '⚠️'}
              </div>
              <p style={{ color: getStatusColor(), fontWeight: 'bold' }}>
                {getStatusMessage()}
              </p>
            </div>
          )}
          
          {accountStatus?.requirements?.currently_due?.length > 0 && (
            <div style={{ 
              marginTop: '1.5rem', 
              padding: '1rem', 
              backgroundColor: '#fff3e0', 
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}>
              <p style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                Additional information needed:
              </p>
              <ul style={{ textAlign: 'left', margin: 0 }}>
                {accountStatus.requirements.currently_due.map((req, index) => (
                  <li key={index}>{req.replace(/_/g, ' ')}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
      
      <StatusMessage
        message={statusMsg.message}
        type={statusMsg.type}
        isVisible={statusMsg.isVisible}
        onClose={statusMsg.hideMessage}
        position="top-center"
      />
    </div>
  );
}

export default StripeConnectReturn;